import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

const subjects = [
  { value: "math", label: "Mathematics" },
  { value: "science", label: "Science" },
  { value: "reading", label: "Reading / ELA" },
  { value: "writing", label: "Writing" },
  { value: "social_studies", label: "Social Studies" },
  { value: "computer_science", label: "Computer Science" },
] as const;

// Sentinel value used by the Select's internal state when the user picks
// "Other (self entry)". It never appears in the parent's `value` prop —
// when in Other mode the parent holds the user's free-text subject.
const OTHER = "__other__";
const PREDEFINED = new Set<string>(subjects.map((s) => s.value));

interface SubjectSelectorProps {
  value: string;
  onChange: (value: string) => void;
  /** Optional class override on the outer wrapper. Useful when the parent
   *  needs to control layout (e.g. flex column with justify-between for
   *  aligning the trigger to the bottom of a grid row). */
  className?: string;
  /** Red border on the SelectTrigger for missing-field validation. */
  hasError?: boolean;
}

export const SubjectSelector = React.forwardRef<HTMLDivElement, SubjectSelectorProps>(
  ({ value, onChange, className, hasError }, ref) => {
    // We're in "Other" mode when the parent's value isn't one of the
    // predefined keys (and isn't empty). Seed from props on mount, and
    // keep in sync with later prop changes (e.g. rehydration from
    // localStorage). Don't auto-exit Other mode on empty so that a user
    // who clears their custom text doesn't lose the input.
    const [otherMode, setOtherMode] = useState<boolean>(() =>
      value !== "" && !PREDEFINED.has(value),
    );
    useEffect(() => {
      if (value !== "" && !PREDEFINED.has(value)) setOtherMode(true);
      else if (PREDEFINED.has(value)) setOtherMode(false);
    }, [value]);

    const selectValue = otherMode ? OTHER : value;
    const inputRef = useRef<HTMLInputElement>(null);

    const handleSelectChange = (next: string) => {
      if (next === OTHER) {
        setOtherMode(true);
        // If the previous selection was a predefined key, clear it so the
        // text input starts empty. If we're already in Other mode (with a
        // custom string), keep what was there.
        if (PREDEFINED.has(value)) onChange("");
        // Focus right after we render the input — done here instead of in
        // a useEffect so we DON'T auto-focus on every mount (e.g. returning
        // to the page with Other already selected). Only fires on the
        // active user choice.
        setTimeout(() => inputRef.current?.focus(), 0);
      } else {
        setOtherMode(false);
        onChange(next);
      }
    };

    const resetToDropdown = () => {
      setOtherMode(false);
      onChange("");
    };

    return (
      <div ref={ref} className={className ?? "space-y-2"}>
        <Label className={`text-base font-semibold ${hasError ? "text-red-600" : ""}`}>
          Subject Area <span className="text-muted-foreground font-normal">(required)</span>
        </Label>
        {otherMode ? (
          // In Other mode the input REPLACES the trigger (same height/width)
          // so the entry field reads as part of the same dropdown control.
          // The X on the right exits Other mode and restores the dropdown.
          <div className="relative">
            <Input
              ref={inputRef}
              type="text"
              placeholder="Enter subject"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className={`h-10 pr-9 ${hasError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              data-testid="input-subject-other"
            />
            <button
              type="button"
              onClick={resetToDropdown}
              aria-label="Clear and choose from list"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded-sm"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <Select value={selectValue} onValueChange={handleSelectChange}>
            <SelectTrigger className={hasError ? "border-red-500 focus-visible:ring-red-500" : ""}>
              <SelectValue placeholder="Select subject" />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((subject) => (
                <SelectItem key={subject.value} value={subject.value}>
                  {subject.label}
                </SelectItem>
              ))}
              <SelectItem value={OTHER}>Other (self entry)</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    );
  },
);
SubjectSelector.displayName = "SubjectSelector";
